<template>
    <div class="online-planner-employees">
        <h3 v-if="!overview" class="section-header__small">
            {{ $t('settings.external-interfaces.online-planner.employees.title') }}
        </h3>
        <p v-if="!overview" class="online-planner-settings__wizard-description">
            {{ $t(`settings.external-interfaces.online-planner.employees.${advanced ? 'advanced-description' : 'description'}`) }}
        </p>
        <p v-if="!overview" class="online-planner-settings__wizard-description" data-testid="online-planner-employees-saved-immediately">
            {{ $t('settings.external-interfaces.online-planner.employees.saved-immediately') }}
        </p>

        <div v-if="loading" class="online-planner-employees__loading" data-testid="online-planner-employees-loading">
            <frm-spinner />
            <strong>{{ $t('settings.external-interfaces.online-planner.employees.loading.title') }}</strong>
        </div>

        <div v-else-if="loadFailed" class="online-planner-employees__empty" data-testid="online-planner-employees-error">
            {{ $t('settings.external-interfaces.online-planner.employees.load-error') }}
            <button class="button button--text" type="button" @click="fetchEmployees">
                {{ $t('settings.external-interfaces.online-planner.employees.retry') }}
            </button>
        </div>

        <div v-else-if="!employees.length" class="online-planner-employees__empty" data-testid="online-planner-employees-empty">
            {{ $t('settings.external-interfaces.online-planner.employees.empty') }}
        </div>

        <div v-else-if="overview" class="online-planner-employees__overview" data-testid="online-planner-employees-overview">
            <p v-if="advanced && missingRoutingCount" class="online-planner-employees__missing-routing" data-testid="online-planner-employees-missing-routing">
                <span class="online-planner-employees__schedule-dot" />
                {{ $tc('settings.external-interfaces.online-planner.employees.missing-routing', missingRoutingCount, {count: missingRoutingCount}) }}
            </p>
            <article
                v-for="employee in orderedEmployees"
                :key="employee.id"
                class="online-planner-employees__overview-card"
                :class="{'online-planner-employees__overview-card--inactive': !employeeScheduled(employee)}"
            >
                <span class="online-planner-employees__avatar">{{ employee.initials }}</span>
                <div class="online-planner-employees__overview-content">
                    <div class="online-planner-employees__overview-name">
                        <strong>{{ employee.name }}</strong>
                        <span
                            v-if="employeeScheduled(employee)"
                            class="online-planner-employees__availability"
                            :class="{'online-planner-employees__availability--online': employee.online_daypart_count}"
                        >
                            {{ employee.online_daypart_count
                                ? $t('settings.external-interfaces.online-planner.overview.available')
                                : $t('settings.external-interfaces.online-planner.overview.unavailable') }}
                        </span>
                    </div>
                    <p v-if="employee.availability_failed" class="online-planner-employees__overview-no-slots">
                        {{ $t('settings.external-interfaces.online-planner.employees.availability-error') }}
                        <button class="button button--text" type="button" @click="fetchAvailability([employee.id])">
                            {{ $t('settings.external-interfaces.online-planner.employees.retry') }}
                        </button>
                    </p>
                    <p v-else-if="!employeeLoaded(employee)" class="online-planner-employees__pending">
                        {{ $t('settings.external-interfaces.online-planner.employees.loading.schedules') }}
                    </p>
                    <p v-else-if="!employeeScheduled(employee)" class="online-planner-employees__overview-no-slots">
                        {{ $t('settings.external-interfaces.online-planner.employees.no-institute-schedules') }}
                    </p>
                    <div v-else-if="employee.online_dayparts && employee.online_dayparts.length" class="online-planner-employees__overview-slots">
                        <span v-for="dayPart in employee.online_dayparts" :key="dayPart.id">
                            <strong>{{ weekday(dayPart.day_of_the_week) }}</strong>
                            {{ formatTime(dayPart.start) }}–{{ formatTime(dayPart.end) }}
                        </span>
                    </div>
                    <p v-else class="online-planner-employees__overview-no-slots">
                        {{ $t('settings.external-interfaces.online-planner.overview.no-slots') }}
                    </p>
                    <div v-if="advanced && employeeScheduled(employee)" class="online-planner-employees__overview-configuration">
                        <p>
                            <span>{{ $t('settings.external-interfaces.online-planner.employees.configuration.specialization-label') }}:</span>
                            {{ specializationLabels(employee).length
                                ? specializationLabels(employee).join(', ')
                                : $t('settings.external-interfaces.online-planner.employees.configuration.not-in-use') }}
                        </p>
                        <div v-if="complaintAreas(employee).length" class="online-planner-employees__overview-chips">
                            <span v-for="complaintArea in complaintAreas(employee)" :key="complaintArea.id">{{ complaintArea.name }}</span>
                        </div>
                        <p v-else class="online-planner-employees__overview-muted">
                            {{ $t('settings.external-interfaces.online-planner.employees.configuration.no-complaint-areas') }}
                        </p>
                        <p v-if="employeeMissesRouting(employee)" class="online-planner-employees__overview-not-findable">
                            <span class="online-planner-employees__schedule-dot" />
                            {{ $t('settings.external-interfaces.online-planner.overview.not-findable') }}
                        </p>
                    </div>
                </div>
                <div class="online-planner-employees__overview-actions">
                    <button v-if="employeeScheduled(employee)" type="button" @click="openSchedules(employee)">
                        {{ $t('settings.external-interfaces.online-planner.overview.edit-schedules') }}
                    </button>
                    <button v-if="advanced && employeeScheduled(employee)" type="button" @click="openConfiguration(employee)">
                        {{ $t('settings.external-interfaces.online-planner.employees.configuration.configure') }}
                    </button>
                </div>
            </article>
        </div>

        <template v-else>
            <p v-if="advanced && missingRoutingCount" class="online-planner-employees__missing-routing" data-testid="online-planner-employees-missing-routing">
                <span class="online-planner-employees__schedule-dot" />
                {{ $tc('settings.external-interfaces.online-planner.employees.missing-routing', missingRoutingCount, {count: missingRoutingCount}) }}
            </p>

            <div class="online-planner-employees__table" :class="{'online-planner-employees__table--advanced': advanced}">
                <div class="online-planner-employees__header" aria-hidden="true">
                    <span>{{ $t('settings.external-interfaces.online-planner.employees.employee') }}</span>
                    <span>{{ $t(`settings.external-interfaces.online-planner.employees.${advanced ? 'schedules-and-settings' : 'schedules'}`) }}</span>
                </div>

                <div
                    v-for="employee in orderedEmployees"
                    :key="employee.id"
                    class="online-planner-employees__row"
                    :class="{'online-planner-employees__row--inactive': !employeeScheduled(employee)}"
                    :data-testid="'online-planner-employee-' + employee.id"
                >
                    <div class="online-planner-employees__identity">
                        <span class="online-planner-employees__avatar">{{ employee.initials }}</span>
                        <span>
                            <strong>{{ employee.name }}</strong>
                            <small>{{ employee.employee_type_label }}</small>
                        </span>
                    </div>

                    <div class="online-planner-employees__details">
                        <div class="online-planner-employees__schedules">
                            <small v-if="employee.availability_failed" :data-testid="'online-planner-employee-availability-error-' + employee.id">
                                {{ $t('settings.external-interfaces.online-planner.employees.availability-error') }}
                                <button class="button button--text" type="button" @click="fetchAvailability([employee.id])">
                                    {{ $t('settings.external-interfaces.online-planner.employees.retry') }}
                                </button>
                            </small>
                            <span v-else-if="!employeeLoaded(employee)" class="online-planner-employees__pending">
                                {{ $t('settings.external-interfaces.online-planner.employees.loading.schedules') }}
                            </span>
                            <small v-else-if="!employeeScheduled(employee)">
                                {{ $t('settings.external-interfaces.online-planner.employees.no-institute-schedules') }}
                            </small>
                            <template v-else>
                                <span
                                    class="online-planner-employees__schedule-count"
                                    :class="{'online-planner-employees__none-online': !employee.online_daypart_count}"
                                >
                                    <frm-icon
                                        v-if="employee.online_daypart_count"
                                        class="online-planner-employees__schedule-icon"
                                        name="calendar-month"
                                    />
                                    <span v-else class="online-planner-employees__schedule-dot" />
                                    {{ availabilityCount(employee) }}
                                </span>
                                <button
                                    class="online-planner-employees__schedule-link"
                                    type="button"
                                    @click="openSchedules(employee)"
                                >
                                    {{ $tc('settings.external-interfaces.online-planner.employees.view-schedules', employee.schedule_count, {count: employee.schedule_count}) }}
                                </button>
                            </template>
                        </div>

                        <div v-if="advanced && employeeScheduled(employee)" class="online-planner-employees__configuration">
                            <p>
                                {{ $t('settings.external-interfaces.online-planner.employees.configuration.specialization-label') }}:
                                <strong v-if="specializationLabels(employee).length">{{ specializationLabels(employee).join(', ') }}</strong>
                                <strong v-else>{{ $t('settings.external-interfaces.online-planner.employees.configuration.not-in-use') }}</strong>
                            </p>
                            <div v-if="complaintAreas(employee).length" class="online-planner-employees__chips">
                                <span v-for="complaintArea in complaintAreas(employee)" :key="complaintArea.id">
                                    {{ complaintArea.name }}
                                </span>
                            </div>
                            <p v-else class="online-planner-employees__configuration-missing">
                                {{ $t('settings.external-interfaces.online-planner.employees.configuration.no-complaint-areas') }}
                            </p>
                            <button class="online-planner-employees__configuration-link" type="button" @click="openConfiguration(employee)">
                                {{ $t('settings.external-interfaces.online-planner.employees.configuration.configure') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import FormatsDayParts from '../../mixins/FormatsDayParts';
import HandlesErrorResponses from '../../mixins/HandlesErrorResponses';
import ModalManager from '../../services/modalManager';
import EmployeeConfiguration from './EmployeeConfiguration.vue';
import EmployeeSchedules from './EmployeeSchedules.vue';
import { enabledSpecializations, missesRouting } from './employeeRouting';

const AVAILABILITY_BATCH_SIZE = 5;
const AVAILABILITY_CONCURRENCY = 2;

export default {
    mixins: [FormatsDayParts, HandlesErrorResponses],

    props: {
        employeesUrl: {
            type: String,
            required: true,
        },

        employeesAvailabilityUrl: {
            type: String,
            required: true,
        },

        employeeUrl: {
            type: String,
            required: true,
        },

        employeeConfigurationUrl: {
            type: String,
            required: true,
        },

        advanced: {
            type: Boolean,
            default: false,
        },

        initialEmployees: {
            type: Array,
            default: () => [],
        },

        initiallyLoaded: {
            type: Boolean,
            default: false,
        },

        overview: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            employees: this.initialEmployees.map(employee => ({ ...employee })),
            loading: !this.initiallyLoaded,
            loadFailed: false,
            availabilityLoading: false,
        };
    },

    computed: {
        orderedEmployees() {
            return [
                ...this.employees.filter(employee => employee.schedule_count !== 0),
                ...this.employees.filter(employee => employee.schedule_count === 0),
            ];
        },

        missingRoutingCount() {
            return this.availabilityLoading ? 0 : this.employees.filter(this.employeeMissesRouting).length;
        },
    },

    mounted() {
        if (this.initiallyLoaded) {
            this.$emit('loaded', true);

            return;
        }

        this.fetchEmployees();
    },

    methods: {
        async fetchEmployees() {
            this.loading = true;
            this.loadFailed = false;
            this.$emit('loaded', false);

            try {
                const { data, complaint_areas: complaintAreas } = (await axios.get(this.employeesUrl)).data;

                this.employees = data.map(employee => ({ ...employee, complaint_areas: complaintAreas }));
            } catch (error) {
                this.loadFailed = true;
                return;
            } finally {
                this.loading = false;
            }

            await this.fetchAvailability(this.employees.map(employee => employee.id));
        },

        async fetchAvailability(employeeIds) {
            const batches = [];

            for (let index = 0; index < employeeIds.length; index += AVAILABILITY_BATCH_SIZE) {
                batches.push(employeeIds.slice(index, index + AVAILABILITY_BATCH_SIZE));
            }

            this.availabilityLoading = true;
            this.applyAvailability(employeeIds.map(id => ({ id, availability_failed: false })));

            const fetchNext = async() => {
                while (batches.length) {
                    const batch = batches.shift();

                    try {
                        const { data } = (await axios.get(this.employeesAvailabilityUrl, { params: { employee_ids: batch } })).data;

                        this.applyAvailability(data);
                    } catch (error) {
                        this.applyAvailability(batch.map(id => ({ id, availability_failed: true })));
                    }
                }
            };

            await Promise.all(Array.from({ length: AVAILABILITY_CONCURRENCY }, fetchNext));
            this.availabilityLoading = false;

            if (this.employees.every(this.employeeLoaded)) {
                this.emitEmployees();
                this.$emit('loaded', true);
            }
        },

        applyAvailability(availability) {
            this.employees = this.employees.map(employee => ({ ...employee, ...availability.find(item => item.id === employee.id) }));
        },

        employeeLoaded(employee) {
            return employee.schedule_count !== undefined;
        },

        employeeScheduled(employee) {
            return employee.schedule_count > 0;
        },

        availabilityCount(employee) {
            if (!employee.online_daypart_count) {
                return this.$t('settings.external-interfaces.online-planner.employees.none-online');
            }

            return this.$t('settings.external-interfaces.online-planner.employees.online-count', {
                online: employee.online_daypart_count,
                total: employee.daypart_count,
            });
        },

        async openSchedules(employee) {
            const updatedEmployee = await ModalManager.openComponentInModal(EmployeeSchedules, {
                employee,
                url: this.employeeUrl.replace('_employee_id_', employee.id),
            }, {}, 'online-planner-schedules');

            if (!updatedEmployee) {
                return;
            }

            const index = this.employees.findIndex(item => item.id === updatedEmployee.id);
            this.$set(this.employees, index, { ...this.employees[index], ...updatedEmployee });
            this.emitEmployees();
        },

        specializationLabels(employee) {
            return enabledSpecializations(employee).map(specialization => specialization.label);
        },

        employeeMissesRouting(employee) {
            return this.employeeScheduled(employee) && missesRouting(employee);
        },

        complaintAreas(employee) {
            return (employee.complaint_area_ids || [])
                .map(id => (employee.complaint_areas || []).find(complaintArea => complaintArea.id === id))
                .filter(Boolean);
        },

        async openConfiguration(employee) {
            const configuration = await ModalManager.openComponentInModal(EmployeeConfiguration, {
                employee,
                url: this.employeeConfigurationUrl.replace('_employee_id_', employee.id),
            }, {
                'configuration-refreshed': this.updateEmployeeConfiguration,
            }, 'online-planner-employee-configuration');

            if (!configuration) {
                return;
            }

            this.updateEmployeeConfiguration(configuration);
        },

        updateEmployeeConfiguration({ complaint_areas: complaintAreas, ...configuration }) {
            this.employees = this.employees.map(employee => ({
                ...employee,
                ...(employee.id === configuration.id ? configuration : {}),
                complaint_areas: complaintAreas || employee.complaint_areas,
            }));
            this.emitEmployees();
        },

        emitEmployees() {
            this.$emit('employees-updated', this.employees.map(employee => ({ ...employee })));
        },
    },
};
</script>
